/** Shared SVG helpers for the d3 renderers. */

const SVG_NS = "http://www.w3.org/2000/svg";

export const LABEL_FONT_SIZE = 11;
export const LABEL_COLOR = "#1a1a1a";
export const AXIS_COLOR = "#000";

export function el<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {}
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

/** Brand mono, matching the Highcharts label style. */
export function labelStyle(node: SVGElement, fontSizePx = LABEL_FONT_SIZE): void {
  node.setAttribute("font-family", "var(--an26-font-mono), monospace");
  node.setAttribute("font-weight", "300");
  node.setAttribute("font-size", `${fontSizePx}px`);
  node.setAttribute("fill", LABEL_COLOR);
}

export function makeSvg(width: number, height: number): SVGSVGElement {
  const svg = el("svg", { width, height, viewBox: `0 0 ${width} ${height}` });
  svg.style.display = "block";
  return svg;
}

/** Absolutely-positioned tooltip host, styled by injectStyles.scss classes. */
export function makeTooltip(mount: HTMLElement): HTMLDivElement {
  const tip = document.createElement("div");
  tip.style.position = "absolute";
  tip.style.pointerEvents = "none";
  tip.style.display = "none";
  tip.style.zIndex = "8";
  mount.style.position = mount.style.position || "relative";
  mount.append(tip);
  return tip;
}

/**
 * Draw now, and redraw whenever the container's width changes.
 *
 * Highcharts reflows on container resize; these renderers compute every position
 * from `mount.clientWidth` at draw time, so without this a rotated phone or a
 * resized tablet keeps the old layout.
 *
 * A `ResizeObserver` on the mount rather than a window `resize` listener, because
 * the container can change size without the window doing so — a tab pane becoming
 * visible, or a surrounding flex layout settling.
 *
 * Two safeguards matter here. Redraws are gated on the *width* actually changing:
 * drawing sets the SVG height, which changes the mount's height, which would
 * re-trigger the observer and loop forever. And each redraw runs the previous
 * draw's cleanup first, so listeners and tooltip nodes do not accumulate.
 */
export function renderResponsive(
  mount: HTMLElement,
  draw: (animateIn: boolean) => () => void
): () => void {
  let cleanup: (() => void) | null = null;
  let lastWidth = -1;
  let frame = 0;
  let drawn = false;

  const run = (): void => {
    const width = Math.round(mount.clientWidth);
    if (width === 0 || width === lastWidth) return;
    lastWidth = width;
    cleanup?.();
    // Animate the entrance once. Highcharts does not re-animate on reflow, and a
    // chart that replays its build every time a container resizes would be worse
    // than one that never animates at all.
    cleanup = draw(!drawn);
    drawn = true;
  };

  run();

  const observer =
    typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(() => {
          cancelAnimationFrame(frame);
          // Coalesce bursts of resize events into one draw per frame.
          frame = requestAnimationFrame(run);
        });
  observer?.observe(mount);

  return () => {
    cancelAnimationFrame(frame);
    observer?.disconnect();
    cleanup?.();
    cleanup = null;
  };
}


/** Highcharts' default series animation duration. */
export const ANIMATION_MS = 1000;

export function prefersReducedMotion(): boolean {
  return (
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Drive an entrance animation with requestAnimationFrame.
 *
 * rAF rather than CSS transitions because the animated values are SVG geometry
 * attributes (`width`, `height`, `y`, `stroke-dashoffset`), and CSS transitions on
 * those are not reliable across browsers.
 *
 * `step` receives eased progress in 0..1 and is called once with 0 before the first
 * frame, so callers never need to set an initial state themselves. Honours
 * prefers-reduced-motion by jumping straight to the finished state.
 */
export function runAnimation(
  step: (progress: number) => void,
  durationMs: number = ANIMATION_MS
): () => void {
  if (prefersReducedMotion()) {
    step(1);
    return () => {};
  }

  // easeInOutSine, matching Highcharts' default series easing.
  const ease = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;

  let raf = 0;
  let start = 0;
  const tick = (now: number): void => {
    start ||= now;
    const t = Math.min(1, (now - start) / durationMs);
    step(ease(t));
    if (t < 1) raf = requestAnimationFrame(tick);
  };

  step(0);
  raf = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(raf);
}
